<template>
    <div :class="['page page--light', { 'page--embed': partnerAppEmbedCustomization }]">
        <section
            v-if="!showKycLevelUpModal"
            class="row gradient-background"
        >
            <div :class="['wrapper', 'wrapper--no-max-width']">
                <header :class="['row__header', {'withdraw__header--arabic': isARLocale}]">
                    <back-button
                        v-if="!isARLocale"
                        :isAbsolute="false"
                        :isBlack="true"
                    />

                    <h1>{{ $t('wallet.send') }}</h1>

                    <back-button
                        v-if="isARLocale"
                        :isAbsolute="false"
                        :isBlack="true"
                        :isArabic="true"
                    />
                </header>

                <wallet-form
                    :title="$t('sendOut.description')"
                    :isButtonDisabled="isButtonDisabled || !showAmountWidget"
                    :isButtonShow="true"
                    :isSidebarVisible="false"
                    :buttonText="$t('global.button.proceed')"
                    :class="['withdraw', {'withdraw--disabled': !showAmountWidget}]"
                    is-withdraw-page
                    @proceedButtonAction="proceedToRecipientDetails"
                >
                    <div
                        class="withdraw__overlay"
                        @click="unlockForm"
                    >
                        <i class="icon-edit" />
                        Edit Amount
                    </div>

                    <div :class="['withdraw__balance']">
                        <label class="withdraw__balance-title">{{ $t('sendOut.balanceText') }}</label>

                        <label
                            id="withdraw-balance"
                            :class="['withdraw__balance-amount', {'withdraw__balance--arabic': isARLocale}]"
                        >
                            {{ balance }}
                            <a
                                id="withdraw-why-link"
                                class="withdraw__balance-why"
                                @click="showWhyModal"
                            >
                                {{ $t('sendOut.why') }}
                            </a>
                        </label>
                    </div>

                    <div class="withdraw__inputs withdraw__inputs--dark">
                        <token-list
                            :activeItem="token"
                            :tokensList="tokensList"
                            @selectToken="selectToken"
                        />

                        <div class="withdraw__input">
                            <input
                                id="input-crypto"
                                v-autofocus="showAmountWidget"
                                :class="['withdraw__input-crypto', {'withdraw__input-crypto--invalid': isInvalidInput}]"
                                type="number"
                                placeholder="0"
                                :value="fiatValue.formatted"
                                @input="calculateValue($event, isCurrencySwitched)"
                            >

                            <label class="withdraw__input-minimum">{{ minimumAmountLabel }}</label>
                            <label class="withdraw__input-fiat">{{ fiatAmountLabel }}</label>

                            <div
                                v-if="isInsufficientBalance"
                                class="limit-reached"
                            >
                                <span class="limit-reached__text">{{ insufficientBalanceText }}</span>
                            </div>

                            <div
                                v-else-if="isSendOutLimitReached"
                                class="limit-reached flex flex--align-center"
                            >
                                <span class="limit-reached__text">{{
                                    $t('sendOut.regulatoryDailyLimit', {
                                        value: Number(formatNumber(truncateValueToDecimalPlaces(regulatoryDailyLimit, 2))),
                                        currency: fiatCurrency.name })+ ')'
                                }}</span>

                                <i
                                    class="limit-reached__icon icon-info"
                                    @click="onClickDailyLimitIcon"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="withdraw__middle-row">
                        <exchange-rate :conversionRateInfo="conversionRateInfo" />
                    </div>
                </wallet-form>

                <wallet-form
                    v-if="showRecipientWidget"
                    :header="$t('sendOut.summary.recipentDetails')"
                    :title="subtitle"
                    class="recipient"
                >
                    <wallet-withdraw-warning
                        :isChz="isChzCurrency"
                        @click.native="showWithdrawInfoModal"
                    />
                    <div class="recipient-list">
                        <recipient-card
                            v-for="(user, index) in userRecipients"
                            :id="`card-recipient-${index}`"
                            :key="index"
                            :recipient="user"
                            @edit="toggleEditRecipientMode"
                            @recipientSelect="setSelectedRecipient"
                            @remove="removeRecipient"
                        />
                    </div>

                    <div :class="['recipient-add', { 'recipient-add--hidden': !userRecipients.length, 'recipient-add--active': showRecipientForm }]">
                        <div
                            class="recipient-add__header"
                            @click="toggleRecipientFormVisibility"
                        >
                            <i class="recipient-add__header-icon icon-plus" />

                            <div class="recipient-add__header-details">
                                <label class="title">{{ recipientHeaderTitle }}</label>
                                <label class="subtitle">{{ $t('walletRecipients.addSubtitle') }}</label>
                            </div>

                            <i
                                id="icon-toggle"
                                class="recipient-add__header-toggle icon-chevron"
                            />
                        </div>

                        <div
                            v-if="showRecipientForm"
                            class="recipient-add__content"
                        >
                            <div class="widget__fields">
                                <label class="label label--bottom-padding">
                                    {{ $t('sendOut.createRecipient.name') }}:

                                    <input
                                        id="input-name"
                                        v-model="recipient.name"
                                        type="text"
                                        :placeholder="$t('sendOut.createRecipient.nameHint')"
                                        :class="['label__input', { 'label__input--active': recipient.name }]"
                                        :disabled="isRecipientActionDisabled"
                                    >

                                    <div
                                        v-if="errors.label"
                                        class="label__error"
                                    >
                                        {{ errors.label }}
                                    </div>
                                </label>

                                <div
                                    v-if="!isEdit && isCC2Enabled"
                                    class="recipient-add__network-list label--bottom-padding"
                                >
                                    <a
                                        v-for="(network, index) in supportWithdrawalToNetworks"
                                        :key="index"
                                        :class="['network-label', { 'is-active' : network.isActive }, {'is-edit' : isEdit}]"
                                        @click="onSelectNetwork(network.name)"
                                    >
                                        {{ $t('sendOut.createRecipient.' + network.name) }}
                                    </a>
                                </div>

                                <label class="label label--bottom-padding">
                                    {{ addressLabel }}:

                                    <input
                                        id="input-address"
                                        v-model="recipient.address"
                                        type="text"
                                        :placeholder="$t('sendOut.createRecipient.addressHint')"
                                        :class="[
                                            'label__input',
                                            'label__input--address',
                                            {
                                                'label__input--active': recipient.address,
                                                'label--disabled' : isEdit
                                            }
                                        ]"
                                        :disabled="isRecipientActionDisabled || isEdit"
                                    >

                                    <button
                                        v-if="!isEdit"
                                        id="paste-button"
                                        class="widget__fields-action"
                                        @click="pasteAddress"
                                    >
                                        {{ $t('global.button.paste') }}
                                    </button>

                                    <div
                                        v-if="errors.network || errors.blockchainAddress"
                                        class="label__error"
                                    >
                                        {{ errors.network || errors.blockchainAddress }}
                                    </div>
                                </label>

                                <div class="label label--bottom-padding">
                                    {{ $t('sendOut.createRecipient.type') }}

                                    <multiselect
                                        id="input-type"
                                        v-model="recipient.type"
                                        :options="sendOutItems"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        track-by="display"
                                        label="display"
                                        :placeholder="$t('sendOut.createRecipient.typeHint')"
                                        :class="['label__input', { 'label__input--active': recipient.type }]"
                                        :disabled="isRecipientActionDisabled"
                                    />
                                </div>
                            </div>

                            <div class="widget__buttons">
                                <button
                                    id="button-add-recipient"
                                    :class="['widget__button', { 'widget__button--disabled': isCreateDisabled || isRecipientActionDisabled }]"
                                    @click="addOrEditRecipient"
                                >
                                    {{ recipientButtonText }}
                                </button>

                                <button
                                    id="button-cancel-recipient"
                                    class="widget__button widget__button-cancel"
                                    @click="unlockForm"
                                >
                                    {{ $t('global.button.cancel') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </wallet-form>

                <confirmation
                    v-if="showConfirmationWidget"
                    class="withdraw-confirmation"
                    :recipient="selectedRecipient"
                    :token="token"
                    :ledgerId="sendOutBalance.ledger.id"
                    @confirm="showConfirmation"
                    @cancel="cancelConfirmation"
                />
            </div>
        </section>
    </div>
</template>

<script>
// 3rd party
import {
    mapGetters,
    mapActions,
} from 'vuex';

import {
    bcmul,
} from 'locutus/php/bc';

// Utils
import {
    formatNumber,
    truncateValueToDecimalPlaces,
} from '@shared/utils/formatter.js';

// Components
import backButton from '@webapp/components/back-button/back-button.vue';
import walletForm from '@webapp/components/wallet/wallet-form.vue';
import exchangeRate from '@webapp/components/exchange-rate/exchange-rate.vue';
import tokenList from '@webapp/components/token-tiles/token-list.vue';
import recipientCard from '@webapp/components/wallet/recipient-card.vue';
import confirmation from '@webapp/components/sendout/confirmation.vue';
import walletWithdrawWarning from '@webapp/modules/crypto-and-trading/components/wallet/wallet-withdraw-warning.vue';

// Socios variables
import socios from '@webapp/socios.json';

// Shared modules
import sharedEpicTypes from '@shared/epics/types.js';
import {
    CurrencyType,
    Network,
} from '@shared/types/currency';

// Mixins
import amountMixin from '@webapp/helpers/amount-mixin.js';
import walletMixin from '@shared/mixins/wallet-mixin.js';
import kycMixin from '@webapp/helpers/kyc-mixin.js';
import userRestrictionMixin from '@webapp/helpers/user-restriction-mixin';

// Local Types
import localEpicTypes from '@webapp/epics/types.js';
import localStoreTypes from '@webapp/store/types.js';
import {
    TransactionType,
} from '@webapp/types/transaction';

// Config
import sendoutConfig from '@shared/config/sendout.js';

// Helpers
import {
    scrollToElement,
    pasteFromClipboard,
} from '@webapp/helpers/generic.js';

import {
    format,
} from '@shared/utils/dates';

export default {
    name: 'wallet-withdraw',
    components: {
        backButton,
        walletForm,
        exchangeRate,
        tokenList,
        recipientCard,
        confirmation,
        walletWithdrawWarning,
    },
    mixins: [
        amountMixin,
        walletMixin,
        kycMixin,
        userRestrictionMixin,
    ],
    props: {
        selectedTokenId: {
            type: String,
            required: false,
            default: socios.chilizCurrencyId,
        },
    },
    data() {
        return {
            mode: 'sendout',
            cryptoBalance: 0,
            fiatBalance: 0,
            token: {},
            chzCurrencyDetails: {
                id: socios.chilizCurrencyId,
                name: 'Chiliz',
                shortName: 'CHZ',
                logoPath: '~/assets/images/chz-logo-icon.webp',
                ledgerType: 'centralized',
            },
            recipient: {
                name: '',
                address: '',
                network: '',
                type: '',
            },
            errors: {},
            sendOutItems: [],
            showAmountWidget: true,
            showRecipientWidget: false,
            showConfirmationWidget: false,
            isRecipientActionDisabled: false,
            isEdit: false,
            showRecipientForm: true,
            showKycLevelUpModal: false,
            selectedRecipient: {},
            supportWithdrawalToNetworks: [],
        };
    },
    computed: {
        ...mapGetters([
            'remoteConfigByKey',
            'getAddedRecipient',
            'getLatestError',
            'userRecipients',
            'getEditedRecipient',
            'getDeletedRecipient',
            'kycTierLevel',
            'isARLocale',
            'partnerAppEmbedCustomization',
        ]),

        ...mapGetters('wallet', [
            'ledgers',
            'wallet',
            'sendOutLimit',
            'sendOutBalance',
            'minimalWithdrawalAmounts',
            'ledgerOverview',
            'isCC2Enabled',
        ]),

        ...mapGetters('voteTokens', ['userTokens']),

        balanceInfo() {
            return {
                fiatBalance: formatNumber(truncateValueToDecimalPlaces(this.sendOutLimit, 2), 'price', 'en-GB') || 0,
                fiatCurrency: this.fiatCurrency.name,
                cryptoBalance: this.fiatBalanceFormatted,
                cryptoCurrency: this.cryptoCurrency.name,
            };
        },
        balance() {
            const {
                fiatBalance,
                fiatCurrency,
                cryptoBalance,
                cryptoCurrency,
            } = this.balanceInfo;

            return `${this.isLoading ? 0 : fiatBalance} ${fiatCurrency} ` +
                `(${this.isLoading ? 0 : cryptoBalance} ${cryptoCurrency})`;
        },
        fiatBalanceFormatted() {
            if (this.currentRate === null || this.sendOutLimit <= 0) {
                return 0;
            }

            return formatNumber(bcmul(this.sendOutLimit, this.currentRate, 2), 'price', 'en-GB');
        },
        tokensList() {
            return this.userTokens(this.chzCurrencyDetails)
                .sort((a, b) => a.name.localeCompare(b.name));
        },
        isChzCurrency() {
            return this.fiatCurrency.name === 'CHZ';
        },

        minChzAmount() {
            return this.minimalWithdrawalAmounts[CurrencyType.Crypto] ?? 0;
        },

        minFTAmount() {
            return this.minimalWithdrawalAmounts[CurrencyType.VoteToken] ?? 0;
        },

        minimumAmount() {
            return this.isChzCurrency ? this.minChzAmount : this.minFTAmount;
        },
        minimumAmountLabel() {
            return this.$t('sendOut.minimumAmount', {
                value: this.minimumAmount,
                currency: this.fiatCurrency.name,
            });
        },
        isButtonDisabled() {
            return Number(this.cryptoValue.original || 0) <= 0 ||
                Number(this.fiatValue.original || 0) < Number(this.minimumAmount || 0) ||
                this.isSendOutLimitReached ||
                this.isInsufficientBalance;
        },
        withdrawTitle() {
            return this.$t('sendOut.title');
        },
        fiatAmountLabel() {
            return `≈  ${this.cryptoValue.formatted} ${this.cryptoCurrency.shortName}`;
        },
        isCreateDisabled() {
            return (!this.recipient.name || !this.recipient.address || !this.recipient.type);
        },
        addressLabel() {
            const activeNetWork = this.activeNetwork.name || '';

            return this.$t('sendOut.createRecipient.' + activeNetWork) + ' ' + this.$t('sendOut.createRecipient.networkAddress');
        },
        subtitle() {
            return this.isEdit ? this.$t('sendOut.editRecipient.subtitle') : this.$t('sendOut.createRecipient.subtitle');
        },
        recipientButtonText() {
            return this.isEdit ? this.$t('sendOut.editRecipient.btns.addRecipient') : this.$t('sendOut.createRecipient.btns.addRecipient');
        },
        recipientHeaderTitle() {
            return this.isEdit ? this.$t('sendOut.editRecipient.subtitle') : this.$t('walletRecipients.addTitle');
        },

        isSendOutLimitReached() {
            return this.regulatoryDailyLimit > 0;
        },

        isInsufficientBalance() {
            return this.fiatValue.original > 0 && (Number(this.sendOutLimit) - this.fiatValue.original) < 0;
        },

        isInvalidInput() {
            return this.isInsufficientBalance || this.isSendOutLimitReached;
        },

        insufficientBalanceText() {
            return this.$t('sendOut.insufficientBalance', {
                remainingBalance: formatNumber(truncateValueToDecimalPlaces(this.sendOutLimit, 2), 'price', 'en-GB'),
                currencyName: this.balanceInfo.fiatCurrency,
            });
        },
        activeNetwork() {
            return this.supportWithdrawalToNetworks.find((network) => network.isActive);
        },
        isCHZSelected() {
            return this.token.shortName === 'CHZ';
        },
    },
    watch: {
        sendOutBalance: 'onSendOutBalance',
        getLatestError: 'onLatestError',
        getLatestSimpleValidationError: 'onLatestError',
        getLatestValidationError: 'onLatestValidationError',
        getAddedRecipient: 'onAddedRecipient',
        getEditedRecipient: 'onEditedRecipient',
        getDeletedRecipient: 'onDeletedRecipient',
        ledgerOverview: {
            handler(ledger) {
                this.supportWithdrawalToNetworks = ledger.ledger?.currencyNetwork?.config?.supportWithdrawalToNetworks
                    .map((object, index) => ({
                        name: object,
                        isActive: this.isCC2Enabled || !this.isChzCurrency ? index === 0 : object === Network.Ethereum,
                    }));
            },
        },
        tokensList(list) {
            if (list?.length && !Object.keys(this.token).length) {
                const initToken = this.tokensList.find(({ currency }) => currency.id === this.selectedTokenId);

                this.selectToken(initToken);
            }
        },
        isLoading: {
            immediate: true,
            handler(newValue) {
                this.showAppLoader({
                    isVisible: newValue,
                    hasOpacity: newValue,
                });
            },
        },
        userRecipients() {
            // Checking whether the fetching of balance/currency rates is still ongoing (isLoading), to avoid removing the app loader in that case
            if (!this.isLoading) {
                this.showAppLoader({
                    isVisible: false,
                });
            }
        },
    },
    async created() {
        this.showAppLoader({
            isVisible: true,
            hasOpacity: true,
        });

        await this.fetchSendOutBalance({
            currencyId: socios.chilizCurrencyId,
        });

        if (!this.wallet.id) {
            await this.getWallet({
                chilizCurrencyId: socios.chilizCurrencyId,
            });
        }

        // eslint-disable-next-line no-undefined
        if (this.minimalWithdrawalAmounts[CurrencyType.Crypto] === undefined) {
            await this.fetchMinimalWithdrawalAmounts(CurrencyType.Crypto);
            await this.fetchMinimalWithdrawalAmounts(CurrencyType.VoteToken);
        }

        await this.checkRegulatoryRestrictionLimit({
            transactionName: TransactionType.Withdrawal,
            isSendOutRegulatoryRestriction: true,
            withFetch: {
                doFetch: true,
                batchType: 'withdrawal',
                movementType: 'debit',
            },
        });

        this.kycMixinSetLevel(this.kycTierLevel);

        this.showAppLoader({
            isVisible: false,
        });
    },
    async mounted() {
        if (this.kycTierLevel < 2) {
            this.displayKycModal();

            return;
        }

        this.populateSendOutItems();

        await this.getRecipients({
            networks: this.supportWithdrawalToNetworks.map((network) => network.name),
        });
    },
    methods: {
        ...mapActions({
            getWallet: sharedEpicTypes.GET_WALLET,
            getCurrencies: sharedEpicTypes.GET_CURRENCIES,
            createRecipient: localEpicTypes.CREATE_RECIPIENT,
            setModal: localStoreTypes.SET_MODAL,
            getRecipients: localEpicTypes.GET_USER_RECIPIENTS,
            editChosenRecipient: localEpicTypes.EDIT_RECIPIENT,
            showAppLoader: localStoreTypes.SET_APP_LOADER,
        }),

        ...mapActions('wallet', [
            'fetchSendOutBalance',
            'setSendOutAmount',
            'fetchMinimalWithdrawalAmounts',
            'fetchLedgerOverview',
        ]),

        truncateValueToDecimalPlaces,

        async onSendOutBalance(balance) {
            if (Object.keys(balance).length > 0) {
                const { ledger } = balance;

                // Update fiat/crypto balances based on 'FiatCurrency' and 'cryptoCurrency'
                // Since they are reversed by walletMixinSwitchCurrencies(), use fiatCurrency instead of cryptoCurrency
                if (ledger && ledger.currency.id === this.fiatCurrency.id) {
                    this.cryptoBalance = Number(ledger.balance || 0) ? ledger.balance : 0;
                }
            }

            const currenciesFromList = [this.cryptoCurrency.id, this.fiatCurrency.id];
            const currenciesToList = [this.fiatCurrency.id, this.cryptoCurrency.id];

            if (this.currencyEuro.id) {
                currenciesFromList.push(this.fiatCurrency.id);
                currenciesToList.push(this.currencyEuro.id);
            }

            await this.walletMixinRefreshCurrencyConversionRates(currenciesFromList, currenciesToList);

            this.calculateValue(null, this.isCurrencySwitched);
        },
        selectToken(token) {
            this.token = token;
            this.onTokenSelect(token);
        },
        async onTokenSelect(item) {
            const updateSendOutBalance = false;

            this.walletMixinResetFiatAndCryptoValues(updateSendOutBalance);
            this.fiatCurrency.id = item.currency.id;
            this.fiatCurrency.name = item.currency.name;
            this.fiatCurrency.decimalDetail = item.currency.decimalDetail;

            await this.fetchSendOutBalance({
                currencyId: item.currency.id,
                ledgerType: item.ledgerType,
            });
            this.calculateValue(null, this.isCurrencySwitched);

            await this.fetchLedgerOverview({
                ledgerId: this.sendOutBalance.ledger.id,
            });

            this.recipient.network = this.activeNetwork.name;

            await this.getRecipients({
                networks: this.supportWithdrawalToNetworks.map((network) => network.name),
            });
        },
        populateSendOutItems() {
            this.sendOutItems = Object.keys(sendoutConfig.recipient.types).map((type) => ({
                value: type,
                display: this.$t(`sendOut.createRecipient.types.${sendoutConfig.recipient.types[type]}`),
            }));
        },
        async proceedToRecipientDetails() {
            const total = Number(this.fiatValue.original || 0);

            // Show KYC cumulative limit modal if the amount of CHZ specified is larger than the user's KYC cumulative limit for sendout
            if (total > Number(this.currentLevel.sendLimit)) {
                this.setModal({
                    isVisible: true,
                    componentToLoad: 'components/wallet/wallet-level-up-modal',
                    data: {
                        kycLevel: this.kycTierLevel,
                        type: 'sendout',
                    },
                });

                return;
            }

            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            /*eslint-disable*/
            await this.setSendOutAmount({
                amount: this.fiatValue.original,
                currency: this.fiatCurrency.name,
                currencyId: this.fiatCurrency.id,
                processStartedAt: format(this.processStartedAt || new Date(), "yyyy-MM-dd'T'HH:mm:ss'%2B'00:00"),
            });

            this.showAmountWidget = false;
            this.showRecipientWidget = true;
            this.showConfirmationWidget = false;

            this.isEdit = false;
            this.resetRecipientData();

            await this.$nextTick(() => {
                scrollToElement('recipient');
            });

            this.showAppLoader({
                isVisible: false,
            });
            
            this.showWithdrawInfoModal();
        },
        showWhyModal() {
            this.setModal({
                isVisible: true,
                componentToLoad: 'components/sendout/why-modal',
            });
        },
        showWithdrawInfoModal() {
            this.setModal({
                isVisible: true,
                componentToLoad: 'modules/crypto-and-trading/components/wallet/wallet-withdraw-info-modal',
                data: {
                    isChz: this.isChzCurrency,
                },
            });
        },
        showConfirmation(reference) {
            this.showAmountWidget = true;
            this.showRecipientWidget = false;
            this.showConfirmationWidget = false;

            this.setModal({
                isVisible: true,
                componentToLoad: 'components/sendout/confirmation-modal',
                data: {
                    reference,
                }
            });
        },
        cancelConfirmation() {
            this.showAmountWidget = false;
            this.showRecipientWidget = true;
            this.showConfirmationWidget = false;

            this.$nextTick(() => {
                scrollToElement('recipient');
            });
        },
        unlockForm() {
            if (!this.showAmountWidget) {
                this.showAmountWidget = true;
                this.showRecipientWidget = false;
                this.showConfirmationWidget = false;

                scrollToElement('withdraw');
            }
        },
        async pasteAddress() {
            this.recipient.address = await pasteFromClipboard(this);
        },
        addRecipient() {
            this.createRecipient({
                label: this.recipient.name.trim(),
                type: this.recipient.type.value,
                network: this.recipient.network,
                blockchainAddress: this.recipient.address.trim(),
            });
        },
        editRecipient() {
            this.editChosenRecipient({
                blockchainAddress: this.recipient.externalAddressId,
                name: this.recipient.name.trim(),
                type: this.recipient.type.value,
                status: 'enabled',
            });
        },
        removeRecipient(recipient) {
            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            this.editChosenRecipient({
                blockchainAddress: recipient.id,
                name: recipient.label,
                type: recipient.type,
                status: 'disabled',
            });
        },
        addOrEditRecipient() {
            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            if (!this.isCreateDisabled) {
                this.isRecipientActionDisabled = true;
                this.errors = {};

                return this.isEdit ? this.editRecipient() : this.addRecipient();
            }
        },
        cancelEditRecipient() {
            this.resetRecipientData();
            this.isRecipientActionDisabled = false;
            this.isEdit = false;

        },
        async onAddedRecipient(newValue) {
            if (Object.keys(newValue).length > 0) {
                await this.refreshRecipientsView(this.$t('sendOut.createRecipient.success'));
                this.isEdit = false;
            }
        },
        async onEditedRecipient(newValue) {
            if (Object.keys(newValue).length > 0) {
                await this.refreshRecipientsView(this.$t('sendOut.editRecipient.success'));
                this.isEdit = false;
            }
        },
        async onDeletedRecipient(newValue) {
            if (Object.keys(newValue).length > 0) {
                await this.refreshRecipientsView(this.$t('sendOut.menu.deletedRecipient'));
                this.isEdit = false;
            }
        },
        async refreshRecipientsView(notifyMessage) {
            this.$notify({
                type: 'success',
                text: notifyMessage,
            });

            await this.updateAddOrEditRecipient();
            this.isEdit = false;
        },
        async updateAddOrEditRecipient() {
            await this.getRecipients({
                networks: this.supportWithdrawalToNetworks.map((network) => network.name),
            });

            this.resetRecipientData();
            this.isRecipientActionDisabled = false;
        },
        async setSelectedRecipient(recipient) {
            this.showAppLoader({
                isVisible: true,
                hasOpacity: true,
            });

            this.selectedRecipient = recipient;
            this.showConfirmationWidget = true;
            this.showAmountWidget = false;
            this.showRecipientWidget = false;

            this.$nextTick(() => {
                scrollToElement('withdraw-confirmation');
            });

            this.showAppLoader({
                isVisible: false,
            });
        },
        onLatestValidationError(newErrors) {
            this.errors = newErrors;
            this.isRecipientActionDisabled = false;

            this.showAppLoader({
                isVisible: false,
            });
        },
        onLatestError({ status }) {
            // If already created recipient will show this error
            if (status === 409) {
                this.$notify({
                    type: 'error',
                    text: this.$t('sendOut.createRecipient.errors.exists'),
                });
            }

            this.showAppLoader({
                isVisible: false,
            });
        },
        toggleRecipientFormVisibility() {
            this.showRecipientForm = !this.showRecipientForm;
        },
        resetRecipientData() {
            this.recipient = {
                name: '',
                address: '',
                type: '',
                network: this.activeNetwork.name,
            };
        },
        toggleEditRecipientMode(recipient) {
            this.isEdit = true;
            scrollToElement('recipient-add');

            this.setActiveNetwork(recipient.network.type);

            this.recipient = {
                name: recipient.label,
                type: this.sendOutItems.find((item) => item.value === recipient.type),
                address: recipient.blockchainAddress,
                externalAddressId: recipient.id,
                network: recipient.network.type,
            };
        },
        displayKycModal() {
            this.showKycLevelUpModal = true;

            this.setModal({
                isVisible: true,
                componentToLoad: 'components/kyc/kyc-level-up-modal',
            });
        },
        onClickDailyLimitIcon() {
            const showButtons = false;

            this.setRegulatoryRestrictionModal(TransactionType.Withdrawal, showButtons);
        },
        onSelectNetwork(newNetworkName) {
            this.setModal({
                isVisible: true,
                componentToLoad: 'modules/crypto-and-trading/components/wallet/wallet-withdraw-fan-tokens-warning-modal',
            });
            this.recipient.network = newNetworkName;
            this.setActiveNetwork(newNetworkName);

        },
        setActiveNetwork(name) {
            this.supportWithdrawalToNetworks = this.supportWithdrawalToNetworks
                .map((network) => {
                    return { name: network.name,  isActive: network.name === name };
                });
        }

    },
};
</script>

<style lang="scss" scoped>
.withdraw {
    position: relative;

    &--disabled {
        &:hover {
            .withdraw__overlay {
                display: flex;
            }
        }

        .withdraw {
            &__inputs,
            &__middle-row {
                opacity: 0.5;
            }
        }
    }

    &__header {
        &--arabic {
            text-align: right;
            padding-right: 30px;
        }
    }

    &__overlay {
        display: none;
        align-items: center;
        justify-content: center;
        color: $white-color;
        position: absolute;
        background: rgba($cyprus-color, 0.9);
        left: 2rem;
        top: 0;
        z-index: 2;
        width: calc(100% - 4rem);
        height: 100%;
        font-weight: bold;
        font-size: 1.8rem;
        border-radius: 1.5rem;
        cursor: pointer;

        i {
            font-size: 4.5rem;
            margin-right: 2rem;
            color: $lima-color;
        }
    }

    &__balance {
        display: flex;
        flex-direction: column;
        line-height: 2rem;
        color: $nobel-grey-color;
        margin-bottom: 1.5rem;

        &--arabic {
            direction: rtl;
        }

        &-title,
        &-why {
            font: 700 1.1rem/1.1rem "Nunito";
        }

        &-amount {
            font-size: 1.1rem;
        }

        &-title {
            color: $dark-gray;
        }

        &-why {
            color: $cornflower-blue-color;
            cursor: pointer;
        }
    }

    &__inputs {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 2.6rem 1rem;
        border-radius: 1.2rem;

        @include min-width(lg) {
            padding: 3.6rem 4.5rem;
        }

        &--dark {
            background: $athens-gray-color;
            padding-bottom: 4.4rem;

            @include min-width(md) {
                padding-bottom: 5rem;
            }

            @include max-width(xl) {
                padding-bottom: 6.4rem;
            }
        }
    }

    &__input {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        max-width: 50%;

        &:first-child {
            text-align: left;
        }

        &:last-child {
            text-align: right;
        }

        &-crypto {
            font: 700 2.6rem/2.6rem "Nunito";
            border: none;
            background: transparent;
            outline: none;
            position: relative;
            top: -0.2rem;
            max-width: 20rem;
            min-width: 5rem;
            color: $cyprus-color;
            appearance: textfield;
            text-overflow: ellipsis;
            box-shadow: none;
            text-align: inherit;
            @include input-color($cyprus-color);

            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
                appearance: none;
                margin: 0;
            }

            @include max-width(sm) {
                top: 0.2rem;
            }

            @include min-width(xl) {
                font-size: 3.6rem;
                line-height: 3.6rem;
                top: 0.5rem;
            }

            &--invalid {
                @include input-color($torch-red-color);
            }
        }

        &-minimum {
            color: $dark-gray;
            font-size: 1.1rem;
            line-height: 1.6rem;

            @include min-width(md) {
                line-height: 2.6rem;
            }
        }

        &-fiat {
            color: $cyprus-color;
            font-size: 1.5rem;
        }
    }

    &__middle-row {
        display: flex;
        padding: 0 4rem;
        justify-content: center;
        align-items: center;
        margin: -1.6rem 0 5rem;

        @include min-width(xl) {
            padding: 0 8.6rem;
        }

        @include max-width(sm) {
            padding: 0 2rem;
        }
    }
}

.limit-reached {
    margin-top: 1rem;

    &__text {
        font-size: 1.2rem;
        color: $torch-red-color;

        @include min-width(lg) {
            font-size: 1.4rem;
        }
    }

    &__icon {
        font-size: 2.2rem;
        margin-left: 1rem;
        color: $cornflower-blue-color;
        background: $white-color;
        border-radius: 50%;
        cursor: pointer;

        @include min-width(lg) {
            font-size: 2.4rem;
        }
    }
}

.network-list {
    display: flex;
}

.network-label {
    border: 1px solid;
    border-radius: 15px;
    padding: 5px 15px;
    height: 30px;
    margin-right: 2rem;
    font-size: 1.5rem;
    cursor: pointer;

    &.is-active {
        background: $loafer-color;
        border-color: $lime-green-color;
        font-weight: bold;
    }

    &.is-edit {
        cursor: default;
    }
}
</style>